import dayjs from '../../plugins/dayjs.ts';
import type {
  TIsoDate,
} from '../../utils/date.ts';
import {
  compareVersions,
} from '../../utils/version.ts';
import type {
  IQuickNavigationItem,
} from '../quick-navigation/quick-navigation.ts';

export type TReleaseHighlightImage = {
  type: 'image',
  /** File in the media folder of the release, e.g. `chart.png`. */
  fileName: string,
};

export type TReleaseHighlightVideo = {
  type: 'video',
  /** MP4 file in the media folder of the release. */
  fileName: string,
  /** Image in the media folder of the release, shown until the video is played. */
  posterFileName: string,
};

/**
 * An SVG drawn with the CSS variables of the theme, e.g. `style="fill: var(--primary-color, #e4693f)"`.
 * It is placed into the page instead of shown as an image, as an image cannot read the variables of
 * the page, so that it follows the selected theme. The fallbacks keep the file viewable on its own.
 */
export type TReleaseHighlightThemedSvg = {
  type: 'themed-svg',
  /** SVG file in the media folder of the release. */
  fileName: string,
};

export type TReleaseHighlightMedia = TReleaseHighlightImage | TReleaseHighlightVideo | TReleaseHighlightThemedSvg;

/** Written in the language it is shown in; the caller passes the highlights of the current locale. */
export type TReleaseHighlight = {
  headline: string,
  media: TReleaseHighlightMedia,
  /** Markdown, rendered without raw HTML. */
  description: string,
};

export type TRelease = {
  version: string,
  releaseDate?: TIsoDate,
  highlights: TReleaseHighlight[],
  /** Markdown with every change of the release, e.g. its section of the changelog. */
  releaseNotes?: string,
};

/**
 * The releases with highlights after `lastKnownVersion` up to `currentVersion`, newest first.
 * Without `lastKnownVersion`, all releases with highlights up to `currentVersion`. With
 * `allowReleasesWithoutHighlights`, releases that only have release notes are included as well.
 */
export function selectReleases({
  releases,
  currentVersion,
  lastKnownVersion,
  allowReleasesWithoutHighlights = false,
}: {
  releases: TRelease[],
  currentVersion: string,
  lastKnownVersion?: string,
  allowReleasesWithoutHighlights?: boolean,
}): TRelease[] {
  return releases
    .filter((release) => release.highlights.length > 0
      || (allowReleasesWithoutHighlights && release.releaseNotes !== undefined))
    .filter((release) => compareVersions(release.version, currentVersion) <= 0)
    .filter((release) => lastKnownVersion === undefined || compareVersions(release.version, lastKnownVersion) > 0)
    .toSorted((first, second) => compareVersions(second.version, first.version));
}

/** The id of the section of a release, which the quick navigation jumps to, e.g. `release-2.14.0`. */
export function createReleaseAnchorId(version: string): string {
  return `release-${version}`;
}

/**
 * One entry per release with the day and month of its release, grouped by the year it was released
 * in. An upcoming release has no date in the changelog yet, so a release without one counts to the
 * year of the release before it. Releases before the first dated one are not grouped.
 */
export function createReleaseQuickNavigationItems({
  releasesNewestFirst,
  locale,
}: {
  releasesNewestFirst: TRelease[],
  locale: string,
}): IQuickNavigationItem[] {
  // The year is the title of the group already. Day.js has no localized format without it.
  const dayAndMonthFormat = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
  });
  const releasesOldestFirst = releasesNewestFirst.toReversed();
  let yearOfPreviousRelease: string | undefined;
  const quickNavigationItemsOldestFirst = releasesOldestFirst.map((release) => {
    if (release.releaseDate !== undefined) {
      yearOfPreviousRelease = String(dayjs(release.releaseDate).year());
    }
    return {
      anchorId: createReleaseAnchorId(release.version),
      label: release.version,
      group: yearOfPreviousRelease,
      detail: release.releaseDate === undefined
        ? undefined
        : dayAndMonthFormat.format(dayjs(release.releaseDate).toDate()),
    };
  });
  return quickNavigationItemsOldestFirst.toReversed();
}
